import { TableResponse } from './api.types'
import { CareArticleType } from './careArticles.types'
import { CertificateRuleType } from './certificate.types'
import { CohortShort } from './cohort.types'
import { Nullish } from './common.types'
import { CourseCompleted } from './courses.types'
import { LessonCompletedInReport } from './lesson.types'
import { IMetricScope } from './metric.types'
import { BaseTableRequest } from './table.types'

export type ViewCountOfLessons = {
  lessonName: string
  courseName: string
  viewCount: number
  numberOfUsers: number
}

export type ViewCountOfLessonsResponse = TableResponse<ViewCountOfLessons[]>

export interface Engagement {
  days: number
  hours: number
  minutes: number
  seconds: number
}

type ActiveEngagementPerUser = {
  userEmail: string
  userFirstName: string
  userLastName: number
  userAccessCode: string
  anonymousID: string
  lessonName: string
  courseName: string
  activeEngagement: Engagement
}

export type ActiveEngagementPerUserResponse = TableResponse<ActiveEngagementPerUser>

export type CourseEngagementPerUser = {
  userEmail: string
  userFirstName: string
  userLastName: number
  userAccessCode: string
  userAnonymousId: string
  courseId: number
  courseName: string
  activeEngagement: Engagement
  userCohorts: CohortShort[]
  completedCourses: CourseCompleted[]
  passedOn: string
}

export type LessonEngagementPerUser = {
  userEmail: string
  userFirstName: string
  userLastName: number
  userAccessCode: string
  userAnonymousId: string
  lessonId: number
  lessonName: string
  courseId: number
  courseName: string
  activeEngagement: Engagement
  userCohorts: CohortShort[]
  completedLessons: LessonCompletedInReport[]
  passedOn: string
}

export type QueryEngagementPerUser = {
  userEmail: string
  userFirstName: string
  userLastName: number
  userAnonymousId: string
  userAccessCode: string
  queryId: number
  queryName: string
  learnArticleId: number
  learnArticleName: string
  activeEngagement: Engagement
  userCohorts: CohortShort[]
}

export type TotalEngagementPerUser = {
  userEmail: string
  userFirstName: string
  userLastName: number
  userAccessCode: string
  anonymousID: string
  totalEngagement: Engagement
  userFirstLoggedInAt: string
  userLastLoggedInAt: string
  userTotalNumbersOfLogin: number
  passedCoursesCount: number
  passedLessonsCount: number
}

// format: YYYY-MM-DD example: 2023-03-01
export interface DateRangeParam {
  fromDate: string
  toDate: string
}

export interface ReportRequestWithDate extends BaseTableRequest {
  params?: DateRangeParam
}

export type TotalEngagementPerUserResponse = TableResponse<TotalEngagementPerUser>

export type TotalEngagementPerOrgUser = {
  userAnonymousId: string
  userFirstName: string
  userLastName: number
  userEmail: string
  organizationName: string
  userType: string
}

export type TotalEngagementPerOrgUsersResponse = TableResponse<TotalEngagementPerOrgUser>

export type EngagementPerSession = {
  anonymousId: number
  firstName: string
  lastName: string
  email: string
  accessCode: string
  startedAt: string
  totalTime: Engagement
}

export type EngagementPerSessionResponse = TableResponse<EngagementPerSession>

export interface ReportRequestSessionDetail extends BaseTableRequest {
  params: {
    sessionUuid: string
  }
}

export interface ReportSession {
  accessCode: Nullish<string>
  anonymousId: number
  duration: Engagement
  email: string
  engagedOn: string
  firstName: string
  lastName: string
  startedAt: string
  userId: number
  uuid: string
}

export interface ReportSessionDetailItem {
  sessionUuid: string
  userId: number
  startedAt: string
  duration: Engagement
  entityType: IMetricScope
  careArticleId: Nullish<number>
  careArticleName: Nullish<string>
  leanArticleId: Nullish<number>
  leanArticleName: Nullish<string>
  lessonId: Nullish<number>
  lessonName: Nullish<string>
  queryId: Nullish<number>
  queryName: Nullish<string>
  questionnaireId: Nullish<number>
  questionnaireName: Nullish<string>
  quizId: Nullish<number>
  quizName: Nullish<string>
}

export interface ReportSessionDetail {
  session: ReportSession
  rows: ReportSessionDetailItem[]
  count: number
}

export type ActiveEngagementPerLesson = {
  lessonName: string
  courseName: string
  audioTime: Engagement
  videoTime: Engagement
  lessonTime: Engagement
  quizTime: Engagement
  totalTime: Engagement
  activeEngagement: Engagement
}

export type ActiveEngagementPerLessonResponse = TableResponse<ActiveEngagementPerLesson>

export type ActiveEngagementPerLearnArticle = {
  learnArticleName: string
  learnTopicName: string
  numberOfUsers: number
  audioTime: Engagement
  videoTime: Engagement
  learnArticleTime: Engagement
  queryTime: Engagement
  totalTime: Engagement
}

export type ActiveEngagementPerLearnArticleResponse = TableResponse<ActiveEngagementPerLearnArticle>

export type ActiveEngagementPerCareArticle = {
  careArticleName: string
  numberOfUsers: number
  audioTime: Engagement
  videoTime: Engagement
  careArticleTime: Engagement
}

export type ActiveEngagementPerCareArticleResponse = TableResponse<ActiveEngagementPerCareArticle>

export type ActiveEngagementPerQuestionnaire = {
  questionnaireName: string
  numberOfUsers: number
  audioTime: Engagement
  questionnaireTime: Engagement
}

export type ActiveEngagementPerQuestionnaireResponse =
  TableResponse<ActiveEngagementPerQuestionnaire>

export type ActiveEngagementPerQuery = {
  audioTime: Engagement
  cohortNames: string
  learnArticleName: string
  numberOfUsers: number
  queryName: string
  queryTime: Engagement
}

export type ActiveEngagementPerQueryResponse = TableResponse<ActiveEngagementPerQuery>

export type ActiveEngagementPerTip = {
  tipName: string
  numberOfUsers: number
  cohortNames: string[]
}

export type ActiveEngagementPerTipResponse = TableResponse<ActiveEngagementPerTip>

export type Quizzes = {
  quizName: string
  completedCount: number
  passedCount: number
  failedCount: number
  passedPercent: number
  failedPercent: number
}

export type QuizzesResponse = TableResponse<Quizzes>

export type EngagementPerUserSearches = {
  anonymousId: number
  firstName: string
  lastName: string
  email: string
  userAccessCode: string
  loginDate: string
  text: string
  type: string
  countOfSearches: number
  countOfResults: number
}

export type EngagementPerUserSearchesResponse = TableResponse<EngagementPerUserSearches>

export type EngagementPerUserCertificate = {
  anonymousId: number
  firstName: string
  lastName: string
  email: string
  userAccessCode: string
  cohorts: CohortShort[]
  name: string
  createdAt: string
  type: CertificateRuleType
  timeSpent: string
}

export type EngagementPerUserCertificateResponse = TableResponse<EngagementPerUserCertificate>

export enum EngagementReportTypes {
  Course = 'course',
  Lesson = 'lesson',
  LearnLibrary = 'learnLibrary',
  CareArticle = 'careArticle',
  Query = 'query',
  Total = 'total',
  Session = 'session',
  Searches = 'searches',
  Certificates = 'certificates',
  Sharing = 'sharing',
}

export enum OrgReportTypes {
  Completion = 'completion',
  Certificates = 'certificates',
}

export type SearchesReport = {
  text: string
  type: string
  countOfSearches: number
  countOfResults: number
}

export type SearchesResponse = TableResponse<SearchesReport>

export type SharedContentReport = {
  lessonId: number
  lessonName: string
  learnArticleId: number
  learnArticleName: string
  careArticleId: number
  careArticleName: string
  careArticleType: CareArticleType
  noteId: number
  noteSubject: string
  contentName: string
  contentType: string
  traineeShareCount: number
  familyShareCount: number
}

export type SharedContentResponse = TableResponse<SharedContentReport>

export interface SharedPerUserReport extends SharedContentReport {
  userId: number
  userEmail: string
  userFirstName: string
  userLastName: string
  userAnonymousId: number
  accessCodeId: number
  userAccessCode: string
  userCohorts: CohortShort[]
  sharedAt: string
  receiverEmail: string
  receiverFirstName: string
  receiverLastName: string
}

export type SharedPerUserResponse = TableResponse<SharedPerUserReport>
